import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { useGetPaymentHistory } from '~entities/payment/api/get-payment-history';
import type { PaymentHistoryInvoice } from '~entities/payment/modal/invoice';

import { ViewReport } from '~features/payment/ui/payment-processing/view-report';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';

import { DateRangePicker } from '../date-range-picker';

const columnHelper = createColumnHelper<PaymentHistoryInvoice>();

interface PaymentProcessingProps {
  type: 'auto' | 'manual';
}

export function PaymentProcessing({ type }: PaymentProcessingProps) {
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(),
    to: new Date(),
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data, isLoading, isFetching } = useGetPaymentHistory({
    startDate: date?.from ? format(new Date(date.from), 'yyyy-MM-dd') : '',
    endDate: date?.to ? format(new Date(date.to), 'yyyy-MM-dd') : '',
  });
  const showInitialLoader = isLoading && !data;

  const columns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='pl-4'
            column={info.column}
            title='Date'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('totalPayments', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='text-right'
            column={info.column}
            title='Total Payments'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('successful', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='text-right'
            column={info.column}
            title='Successful'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('failed', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='text-right'
            column={info.column}
            title='Failed'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('totalAmount', {
        header: (info) => (
          <DataTableColumnHeader
            disableHiding
            className='text-right'
            column={info.column}
            title='Total Amount'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <div className=''>Actions</div>,
        cell: (info) => (
          <ViewReport date={info.row.original.date} type={type} />
        ),
      }),
    ],
    [type],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {type === 'auto' ? 'Automatic' : 'Manual'} Payment Processing History
        </CardTitle>
        <CardDescription>
          View all {type === 'auto' ? 'automatically' : 'manually'} processed
          payments grouped by date
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showInitialLoader ? (
          <div className='flex h-32 items-center justify-center'>
            <div className='size-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary' />
          </div>
        ) : (
          <EnrollioTable
            ariaLabel={`${type === 'auto' ? 'Automatic' : 'Manual'} payment processing history`}
            totalLabel='Processing dates'
            filterHeader={<DateRangePicker date={date} setDate={setDate} />}
            toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
            columnFilters={columnFilters}
            columnVisibility={columnVisibility}
            columns={columns}
            data={data ?? []}
            enableRowSelection={true}
            isUpdating={isFetching}
            onColumnFiltersChange={setColumnFilters}
            onColumnVisibilityChange={setColumnVisibility}
            onRowSelectionChange={setRowSelection}
            onSortingChange={setSorting}
            rowSelection={rowSelection}
            showSelectionCount
            sorting={sorting}
          />
        )}
      </CardContent>
    </Card>
  );
}
